/**
 * 자가측정 서류관리
 * ---------------------------------------------------------------------------
 * 별지 제7호서식의 "3. 자가측정사항" 은 측정을 대행업자에게 전부 위탁하면
 * 작성하지 않아도 된다. 대신 대행업체가 준 성적서를 보관하고, 운영기록부를
 * 인쇄할 때 그 서류를 가리킨다.
 *
 * 제목이 파일명으로 채워지는 이유
 *   대행업체가 보내는 파일 이름이 이미 "2025년 상반기 자가측정성적서.pdf"
 *   같은 형태다. 사람이 다시 옮겨 적을 이유가 없다. 채워 준 뒤 고칠 수 있게
 *   둔다 — 이미 제목을 손본 경우에는 덮어쓰지 않는다.
 *
 * 연도·반기도 파일명에서 읽는다
 *   같은 이유다. 못 읽으면 그대로 둔다. 잘못 채우느니 비워 두는 쪽이 낫다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import { DownloadOutlined, EyeOutlined, InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { SelfMeasurement, Site } from '@eco/shared';
import { ECO_PERMISSIONS } from '@eco/shared';
import { api, errorMessage } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { siteOptions } from '../lib/siteOptions';
import { can, useSession } from '../lib/session';
import { HelpH, HelpList, HelpP, PageHelp } from '../components/PageHelp';
import { FilePreview, type PreviewTarget } from '../components/FilePreview';

interface FormValues {
  siteId: string;
  title: string;
  measuredYear: number;
  half: 1 | 2 | null;
  agencyName: string | null;
  measuredOn: dayjs.Dayjs | null;
  note: string | null;
}

const HALF_OPTIONS = [
  { value: 1, label: '상반기' },
  { value: 2, label: '하반기' },
];

export function SelfMeasurementPage() {
  const queryClient = useQueryClient();
  const { message } = AntApp.useApp();
  const { data: session } = useSession();
  const manage = can(session, ECO_PERMISSIONS.SELF_MEASURE_MANAGE);

  const [form] = Form.useForm<FormValues>();
  const [editing, setEditing] = useState<SelfMeasurement | null>(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [siteFilter, setSiteFilter] = useState<string | undefined>();
  /** 미리보기로 열어 둔 성적서. null 이면 닫힌 상태다. */
  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  const sites = useQuery<Site[]>({
    queryKey: ['sites'],
    queryFn: async () => (await api.get<Site[]>('/sites')).data,
  });

  const docs = useQuery<SelfMeasurement[]>({
    queryKey: ['self-measurements', siteFilter],
    queryFn: async () =>
      (await api.get<SelfMeasurement[]>('/self-measurements', { params: { siteId: siteFilter } }))
        .data,
  });

  const startCreate = () => {
    setEditing(null);
    setFile(null);
    setFileList([]);
    form.resetFields();
    form.setFieldsValue({
      siteId: siteFilter ?? sites.data?.[0]?.siteId,
      measuredYear: dayjs().year(),
      half: null,
    } as Partial<FormValues> as FormValues);
    setOpen(true);
  };

  const startEdit = (row: SelfMeasurement) => {
    setEditing(row);
    setFile(null);
    setFileList([]);
    form.setFieldsValue({
      siteId: row.siteId,
      title: row.title,
      measuredYear: row.measuredYear,
      half: row.half,
      agencyName: row.agencyName,
      measuredOn: row.measuredOn ? dayjs(row.measuredOn) : null,
      note: row.note,
    });
    setOpen(true);
  };

  const submit = useMutation({
    mutationFn: async (values: FormValues) => {
      // multipart 로 보낸다. 값은 전부 문자열이 되고 서버 스키마가 되돌린다.
      const body = new FormData();
      body.append('siteId', values.siteId);
      body.append('title', values.title);
      body.append('measuredYear', String(values.measuredYear));
      body.append('half', values.half === null || values.half === undefined ? '' : String(values.half));
      body.append('agencyName', values.agencyName ?? '');
      body.append('measuredOn', values.measuredOn ? values.measuredOn.format('YYYY-MM-DD') : '');
      body.append('note', values.note ?? '');
      if (file) body.append('file', file);

      if (editing) {
        await api.put(`/self-measurements/${editing.selfMeasurementId}`, body);
        return;
      }
      await api.post('/self-measurements', body);
    },
    onSuccess: async () => {
      message.success(editing ? '수정했습니다.' : '등록했습니다.');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['self-measurements'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`/self-measurements/${id}`),
    onSuccess: async () => {
      message.success('삭제했습니다.');
      await queryClient.invalidateQueries({ queryKey: ['self-measurements'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  /** 파일을 내려받는다. 인증 헤더가 필요하므로 링크가 아니라 요청으로 받는다. */
  const download = async (row: SelfMeasurement) => {
    try {
      const res = await api.get(`/self-measurements/${row.selfMeasurementId}/file`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = row.fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error(errorMessage(err, '파일을 내려받지 못했습니다.'));
    }
  };

  return (
    <Card
      title="자가측정 서류관리"
      extra={
        <Space>
          <PageHelp title="자가측정 서류관리 — 화면 설명" withLabel>
            <HelpP>
              측정 대행업체가 보내 준 <b>자가측정 성적서를 보관하는 곳</b>입니다. 여기에
              올려 두면 <b>대기 운영기록부</b>를 인쇄할 때 「3. 자가측정사항」 자리에 그
              서류를 가리킬 수 있습니다.
            </HelpP>

            <HelpH>왜 성적서를 보관만 하면 되나</HelpH>
            <HelpP>
              별지 제7호서식의 「3. 자가측정사항」은 측정을 대행업자에게 전부 위탁한
              경우 직접 작성하지 않아도 됩니다. 대신 대행업체의 성적서를 보관하고
              기록부에서 그것을 가리키면 됩니다.
            </HelpP>

            <HelpH>이 서류는 대기 운영기록부에만 쓰입니다</HelpH>
            <HelpP>
              폐수 위탁처리(별지 제20호)와 폐수 자가운영일지(별지 제18호)에는 쓰이지
              않습니다. 그 두 서식에는 자가측정 항목 자체가 없습니다.
            </HelpP>

            <HelpH>등록 순서</HelpH>
            <HelpList
              items={[
                { term: '1. 서류 등록', desc: '대행업체가 보낸 PDF 를 그대로 올립니다.' },
                {
                  term: '2. 제목·연도·반기 확인',
                  desc: '파일 이름에서 자동으로 읽어 채웁니다. 틀리면 고치세요.',
                },
                {
                  term: '3. 대기 운영기록부에서 선택',
                  desc: '일지 화면의 「자가측정 성적서」에서 이 서류를 고르면 인쇄물에 반영됩니다.',
                },
              ]}
            />

            <HelpH>내려받지 않고 바로 보기</HelpH>
            <HelpP>
              목록의 <b>파일명을 누르면</b> 성적서가 창 안에서 바로 열립니다. 받아서
              열어 볼 필요 없이 「이게 그 반기 것이 맞나」를 확인할 수 있습니다. 파일로
              보관하려면 파일명 옆의 내려받기 단추를 쓰세요.
            </HelpP>
          </PageHelp>

          {/*
            목록만 받는다. 성적서 PDF 자체는 파일 칸에서 한 건씩 내려받는다 —
            여러 PDF 를 한 파일로 묶는 것은 여기서 할 일이 아니다.
            목록에 파일명이 들어 있어 받은 PDF 와 맞춰 볼 수 있다.
          */}
          <Button
            icon={<DownloadOutlined />}
            disabled={(docs.data ?? []).length === 0}
            onClick={() =>
              downloadCsv('자가측정서류', docs.data ?? [], [
                { header: '사업장', value: (r) => r.siteName },
                { header: '측정연도', value: (r) => r.measuredYear },
                {
                  header: '반기',
                  value: (r) => (r.half ? (r.half === 1 ? '상반기' : '하반기') : ''),
                },
                { header: '제목', value: (r) => r.title },
                { header: '측정대행업체', value: (r) => r.agencyName },
                { header: '측정일', value: (r) => r.measuredOn },
                { header: '파일명', value: (r) => r.fileName },
                { header: '파일크기(byte)', value: (r) => r.fileSize },
              ])
            }
          >
            엑셀 내려받기
          </Button>

          {manage && (
            <Button type="primary" onClick={startCreate} disabled={(sites.data ?? []).length === 0}>
              서류 등록
            </Button>
          )}
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="측정 대행업체가 보내 준 자가측정 성적서를 보관하는 곳입니다."
        description={
          <>
            여기에 올려 둔 서류는 <b>대기 운영기록부(별지 제7호서식)</b> 의 「3.
            자가측정사항」 자리에 쓰입니다. 폐수 위탁처리·폐수 자가운영일지에는 쓰이지
            않습니다.
          </>
        }
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="사업장 전체"
          style={{ width: 240 }}
          value={siteFilter}
          onChange={setSiteFilter}
          options={siteOptions(sites.data)}
        />
      </Space>

      <Table<SelfMeasurement>
        rowKey="selfMeasurementId"
        loading={docs.isLoading}
        dataSource={docs.data ?? []}
        pagination={false}
        locale={{
          emptyText:
            '등록된 자가측정 성적서가 없습니다. 대행업체에게 받은 PDF 를 올려 두면 운영기록부 인쇄에 함께 쓰입니다.',
        }}
        columns={[
          { title: '사업장', dataIndex: 'siteName', width: 200 },
          {
            title: '대상 기간',
            width: 140,
            render: (_, row) => (
              <Space size={4}>
                <span>{row.measuredYear}년</span>
                {row.half && <Tag>{row.half === 1 ? '상반기' : '하반기'}</Tag>}
              </Space>
            ),
          },
          { title: '제목', dataIndex: 'title' },
          { title: '측정대행업체', dataIndex: 'agencyName', width: 180, render: (v) => v ?? '-' },
          { title: '측정일', dataIndex: 'measuredOn', width: 120, render: (v) => v ?? '-' },
          {
            /*
              파일명을 누르면 **미리보기**가 열린다. 내려받기는 그 옆에 둔다.

              여기 쌓이는 것은 측정 대행업체가 보낸 성적서 PDF 이고, 대개는
              「이게 그 반기 것이 맞나」를 한 번 확인하면 끝이다. 그때마다
              내려받게 하면 받은 파일만 쌓인다.
            */
            title: '파일',
            width: 260,
            render: (_, row) => (
              <Space size={0}>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() =>
                    setPreview({
                      fileName: row.fileName,
                      contentType: 'application/pdf',
                      load: async () =>
                        (
                          await api.get(`/self-measurements/${row.selfMeasurementId}/file`, {
                            responseType: 'blob',
                          })
                        ).data as Blob,
                    })
                  }
                >
                  {row.fileName}
                </Button>
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  title={`내려받기 (${formatBytes(row.fileSize)})`}
                  onClick={() => void download(row)}
                />
              </Space>
            ),
          },
          {
            title: '',
            width: 130,
            render: (_, row) =>
              manage && (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => startEdit(row)}>
                    수정
                  </Button>
                  <Popconfirm
                    title="서류 삭제"
                    description="올린 파일도 함께 지워집니다."
                    okText="삭제"
                    cancelText="취소"
                    onConfirm={() => remove.mutate(row.selfMeasurementId)}
                  >
                    <Button type="link" size="small" danger>
                      삭제
                    </Button>
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />

      <Modal
        open={open}
        title={
          <Space>
            <span>{editing ? '자가측정 서류 수정' : '자가측정 서류 등록'}</span>
            <PageHelp title="자가측정 서류 — 항목 설명">
              <HelpP>
                대행업체가 보낸 PDF 를 그대로 올리면 됩니다. 제목·연도·반기는 파일
                이름에서 읽어 자동으로 채웁니다. 틀리면 고치세요.
              </HelpP>
              <HelpList
                items={[
                  { term: '파일 (PDF)', desc: '대행업체가 보내 준 성적서 원본입니다.' },
                  {
                    term: '사업장',
                    desc: '이 성적서가 어느 사업장 것인지. 그 사업장의 대기 운영기록부에서만 고를 수 있습니다.',
                  },
                  { term: '제목', desc: '목록과 인쇄물에서 이 서류를 가리키는 이름입니다.' },
                  {
                    term: '측정 연도 · 반기',
                    desc: '자가측정은 보통 반기마다 합니다. 인쇄할 때 가장 최근 것이 기본으로 선택됩니다.',
                  },
                  { term: '측정일', desc: '성적서에 적힌 실제 측정 날짜입니다.' },
                  { term: '측정대행업체', desc: '성적서를 발행한 업체 이름입니다.' },
                ]}
              />
            </PageHelp>
          </Space>
        }
        okText="저장"
        cancelText="취소"
        confirmLoading={submit.isPending}
        onCancel={() => setOpen(false)}
        onOk={() => {
          void form.validateFields().then((values) => {
            if (!editing && !file) {
              message.warning('PDF 파일을 첨부해 주세요.');
              return;
            }
            submit.mutate(values);
          });
        }}
        destroyOnHidden
        width={620}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="파일 (PDF)" required={!editing}>
            <Upload.Dragger
              accept="application/pdf,.pdf"
              maxCount={1}
              fileList={fileList}
              // 서버로 직접 올리지 않는다. 다른 값과 함께 한 번에 보낸다.
              beforeUpload={(picked) => {
                setFile(picked);
                setFileList([{ uid: picked.name, name: picked.name, status: 'done' }]);
                applyFileName(form, picked.name);
                return false;
              }}
              onRemove={() => {
                setFile(null);
                setFileList([]);
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">PDF 파일을 끌어다 놓거나 눌러서 고르세요</p>
              <p className="ant-upload-hint">
                {editing
                  ? '파일을 붙이지 않으면 기존 파일이 유지됩니다.'
                  : '파일명이 제목·연도·반기에 자동으로 채워집니다.'}
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item name="siteId" label="사업장" rules={[{ required: true, message: '사업장을 고르세요' }]}>
            <Select
              options={siteOptions(sites.data)}
            />
          </Form.Item>

          <Form.Item name="title" label="제목" rules={[{ required: true, message: '제목을 입력하세요' }]}>
            <Input maxLength={300} />
          </Form.Item>

          <Space size={12} style={{ display: 'flex' }} align="start">
            <Form.Item
              name="measuredYear"
              label="측정 연도"
              rules={[{ required: true, message: '연도를 입력하세요' }]}
            >
              <InputNumber min={2000} max={2200} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="half" label="반기">
              <Select allowClear placeholder="연 1회면 비움" style={{ width: 160 }} options={HALF_OPTIONS} />
            </Form.Item>
            <Form.Item name="measuredOn" label="측정일">
              <DatePicker style={{ width: 160 }} />
            </Form.Item>
          </Space>

          <Form.Item name="agencyName" label="측정대행업체">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="note" label="비고">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      <FilePreview target={preview} onClose={() => setPreview(null)} />
    </Card>
  );
}

/**
 * 파일명에서 제목·연도·반기를 채운다.
 *
 * 이미 사용자가 적어 둔 값은 건드리지 않는다. 파일을 다시 붙였다고 손본
 * 제목이 사라지면 그때부터 자동 입력은 방해다.
 */
function applyFileName(form: ReturnType<typeof Form.useForm<FormValues>>[0], fileName: string) {
  const base = fileName.replace(/\.pdf$/i, '').trim();

  if (!form.getFieldValue('title')) form.setFieldValue('title', base.slice(0, 300));

  const year = /(20\d{2})\s*년?/.exec(base);
  if (year) form.setFieldValue('measuredYear', Number(year[1]));

  if (/상\s*반기|1\s*차|상반기/.test(base)) form.setFieldValue('half', 1);
  else if (/하\s*반기|2\s*차|하반기/.test(base)) form.setFieldValue('half', 2);
}

function formatBytes(size: number): string {
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)}KB`;
  return `${(size / 1024 / 1024).toFixed(1)}MB`;
}
